import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { optToPromise } from '@commons/util/utilities/option';
import { IHttpService, IPromise, isDefined } from 'angular';
import { catchError, EMPTY, filter, map, Observable } from 'rxjs';

import {
  AgentStatus,
  BookingStatus,
  ChannelType,
  Configuration,
  CSR_FACADE_PREFIX,
  isString,
  MessagingService,
  ProfileFacade,
  ProfileService,
} from '@contact/upgrade';

import { IAgentStatusDeprecatedService } from '../agent/agent-status-deprecated.service';
import { IContactDeprecatedService } from '../contact/contact-deprecated.service';
import { IResponseAttachment, ISemanticAnalysisResponse } from './models/knowledge-base-data';

const MODULE_NAME = 'knowledgeBaseService';
export const RESET_KNOWLEDGEBASE_FRAME = 'RESET_KNOWLEDGEBASE_FRAME';
export const KNOWLEDGEBASE_RESULT_AVAILABLE = 'MODEL_OF_RESPONSE_AVAILABLE';

const KNOWLEDGE_BASE_FRAME_NAME = 'agentKnowledgeBase';

export interface IKnowledgeBaseService {
  // the proposal are not correct
  notifyAnswerKO(idExt: string): Observable<boolean>;
  // the skills/routing of the proposal are not correct
  notifySkillsKO(idExt: string): Observable<boolean>;
  // the CSR cleared the mail content on the response
  notifyClearDraft(idExt: string): Observable<boolean>;
  // get the response type matching the mail id
  getReponseType(idExt: string): Observable<ISemanticAnalysisResponse>;
  // request to reset the knowledge base frame.
  resetKnowledgeBaseFrame(): void;
  // create the knowledgeBaseFrame
  // createFrame(): void;
  // show the knowledgeBaseFrame
  showFrame(container: HTMLElement);
  // hide the knowledgeBaseFrame
  hideFrame();
  // should we display the KO notification button ?
  displayNotificationErrorButton(): boolean;
  // return the KnowledgeRequestId
  getKnowledgeRequestId(): string;

  loadUrl(channelType: ChannelType, requestId: string);
}

export const blobFromBase64String = (base64String, type) => {
  const byteArray = Uint8Array.from(
    atob(base64String)
      .split('')
      .map(char => char.charCodeAt(0))
  );
  return new Blob([byteArray], { type: type });
};

export class KnowledgeBaseService implements IKnowledgeBaseService {
  static $inject: Array<string> = [
    '$q',
    '$log',
    'httpDeprecated',
    '$http',
    'configuration',
    'messaging',
    'externalApplications',
    'profileService',
    'agentStatusDeprecatedService',
    'contactDeprecatedService',
    'profileFacade',
  ];

  public name = MODULE_NAME;

  private knowledgeBaseUrl: string = '';
  private knowledgeRequestId: string = '';
  private isDisplayNotificationErrorButton: boolean = false;
  private knowledgeBaseFrame: IPromise<HTMLIFrameElement>;
  private isKnowledgeBaseFrameEnabled = this.configuration.isKnowledgeBaseFrameEnabled ?? false;

  private currentTimeout: any;
  private MAX_NB_CALLS: number = 5;
  private TIME_BETWEEN_CALLS: number = 5 * 1000;

  constructor(
    private readonly $q: ng.IQService,
    private readonly $log: angular.ILogService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly $http: IHttpService,
    protected readonly configuration: Configuration,
    private readonly messaging: MessagingService,
    private readonly appExternalService: AppExternalService,
    public readonly profileService: ProfileService,
    private readonly agentStatusDeprecatedService: IAgentStatusDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly profileFacade: ProfileFacade
  ) {
    this.profileFacade.profile$.subscribe(profile => {
      if (profile.domainId === '') {
        return;
      }
      if (isString(this.configuration.knowledgeBaseServiceUrl)) {
        this.knowledgeBaseUrl = this.configuration.knowledgeBaseServiceUrl;
        this.$log.info('Will use [' + this.knowledgeBaseUrl + '] as knowledgeBaseServiceUrl.');
      } else {
        if (profile.domainId && this.configuration.knowledgeBaseServiceUrl?.hasOwnProperty(profile.domainId)) {
          this.knowledgeBaseUrl = this.configuration.knowledgeBaseServiceUrl[profile.domainId];
          this.$log.info('Will use [' + this.knowledgeBaseUrl + '] as domain knowledgeBaseServiceUrl.');
        } else {
          this.$log.warn('The domain uuid [' + profile.domainId + '] knowledgeBaseServiceUrl is not set !');
        }
      }

      if (this.profileService.hasPermission('CSR::answer_models:display') && this.isKnowledgeBaseFrameEnabled) {
        this.createFrame().then(() => {
          // Booking in process (F5 ?)
          this.contactDeprecatedService
            .contactState$()
            .pipe(filter(event => event.state === BookingStatus.IN_PROCESS))
            .subscribe(event => {
              // load only if knowledgeRequestId doesn't exist
              if (!this.knowledgeRequestId) {
                this.isDisplayNotificationErrorButton = true;
                this.loadUrl(event.contact.channel.type, `${event.contact.epticaRequestId}`);
              }
            });

          // Booking state allocated
          this.contactDeprecatedService
            .contactState$()
            .pipe(filter(event => event.state === BookingStatus.ALLOCATED))
            .subscribe(event => {
              this.isDisplayNotificationErrorButton = true;
              this.loadUrl(event.contact.channel.type, `${event.contact.epticaRequestId}`);
            });

          // Booking state closed
          this.contactDeprecatedService
            .contactState$()
            .pipe(filter(event => event.state === BookingStatus.CLOSED))
            .subscribe(() => {
              this.isDisplayNotificationErrorButton = false;
              this.clean();
              this.loadUrl();
            });

          if (this.agentStatusDeprecatedService.getCurrentAgentState() === AgentStatus.AVAILABLE) {
            this.loadUrl();
          }
        });
      }
    });
  }

  // show the knowledgeBaseFrame
  public showFrame(container: HTMLElement): void {
    this.appExternalService.showApplication(KNOWLEDGE_BASE_FRAME_NAME);

    const positionApplicationWhenIsVisible = setInterval(() => {
      if (this.appExternalService.isVisible(KNOWLEDGE_BASE_FRAME_NAME)) {
        this.appExternalService.positionApplication(KNOWLEDGE_BASE_FRAME_NAME, container);
        clearInterval(positionApplicationWhenIsVisible);
      }
    }, 100);
  }

  // hide the knowledgeBaseFrame
  public hideFrame(): void {
    this.appExternalService.hideApplication(KNOWLEDGE_BASE_FRAME_NAME);
  }

  public displayNotificationErrorButton(): boolean {
    return this.isDisplayNotificationErrorButton;
  }

  public getKnowledgeRequestId(): string {
    return this.knowledgeRequestId;
  }

  public loadUrl(channelType?: ChannelType, requestId?: string) {
    this.knowledgeRequestId = '';
    // Get contact informations
    if (isDefined(channelType)) {
      if (channelType === ChannelType.MAIL || channelType === ChannelType.CHAT || channelType === ChannelType.SOCIAL_NETWORK) {
        this.knowledgeRequestId = requestId || '';
      }
      this.knowledgeBaseFrame.then(frame => this.replaceUrl(frame, this.knowledgeRequestId));
    } else {
      this.knowledgeBaseFrame.then(frame => this.replaceUrl(frame));
    }
  }

  private clean() {
    this.knowledgeRequestId = '';
  }

  private requestKnowledgeBaseAction(action: string, idExt: string): Observable<boolean> {
    let config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true',
        'Content-Type': 'text/plain; charset=UTF-8',
        Accept: 'text/plain; charset=UTF-8',
      },
    };
    return Observable.create(subscriber => {
      this.$http
        .get(this.configuration.knowledgeBaseServiceUrl + 'AKBEvt/action=notify_ko?idExt=' + idExt + '&code=' + action, config)
        .then(
          success => {
            if (success.status === 200) {
              subscriber.next(success.data);
              subscriber.complete();
            } else {
              subscriber.error(JSON.stringify(success));
            }
          },
          error => {
            this.$log.error(`[${MODULE_NAME}] notify_ko [${action}] FAILED for [${idExt}] with error[${JSON.stringify(error)}]`);
            subscriber.error(error);
          }
        );
    });
  }

  private buildAttachments(att): IResponseAttachment[] {
    return att.map(elem => ({
      name: elem.name,
      content: elem.content,
      blob: blobFromBase64String(elem.content, 'text/plain'),
      inline: elem.inline,
    }));
  }

  private buildResponseType(response): ISemanticAnalysisResponse {
    let ret: ISemanticAnalysisResponse = {
      responseText: response.responseText,
      nbOfModelOfResponses: response.nbModelOfResponse,
      attachments: [],
    };
    if (!!response) {
      ret = {
        ...ret,
        attachments: this.buildAttachments(response.attachments),
      };
    }
    return ret;
  }

  private getSemanticAnalysisResponse(idExt: string): Observable<ISemanticAnalysisResponse> {
    let config = { timeout: 1000 };

    return this.httpDeprecated
      .asObservable()
      .withConfig(config)
      .get(CSR_FACADE_PREFIX + '/getSemanticAnalysisResponse', [idExt])
      .pipe(
        map(success => {
          this.$log.debug(`[${MODULE_NAME}] getSemanticAnalysisResponse OK for [${idExt}]`);
          if (success.status === 200) {
            let ret: ISemanticAnalysisResponse = this.buildResponseType(success.data);
            if (!!ret && ret.nbOfModelOfResponses > 0) {
              this.messaging.forLocal().publish(KNOWLEDGEBASE_RESULT_AVAILABLE, undefined);
            }
            return ret;
          }
          throw new Error('status [' + success + '] != 200');
        }),
        catchError(err => {
          this.$log.error(`[${MODULE_NAME}] getSemanticAnalysisResponse FAILED for [${idExt}] with error[${JSON.stringify(err)}]`);
          return EMPTY;
        })
      );
  }

  notifyAnswerKO(idExt: string): Observable<boolean> {
    this.isDisplayNotificationErrorButton = false;
    return this.requestKnowledgeBaseAction('ko_reponse', idExt);
  }

  notifySkillsKO(idExt: string): Observable<boolean> {
    return this.requestKnowledgeBaseAction('ko_tri', idExt);
  }

  notifyClearDraft(idExt: string): Observable<boolean> {
    return this.requestKnowledgeBaseAction('effacer_brouillon', idExt);
  }

  getReponseType(idExt: string): Observable<ISemanticAnalysisResponse> {
    return this.getSemanticAnalysisResponse(idExt);
  }

  resetKnowledgeBaseFrame(): void {
    // RESET_KNOWLEDGEBASE_FRAME
    this.messaging.forLocal().publish(RESET_KNOWLEDGEBASE_FRAME, undefined);
    optToPromise(this.appExternalService.getApplicationContainer(KNOWLEDGE_BASE_FRAME_NAME), this.$q).then(frame => {
      this.knowledgeRequestId = '';
      this.replaceUrl(frame, '', this.knowledgeBaseUrl);
    });
    return;
  }

  private createFrame(): IPromise<HTMLIFrameElement> {
    return optToPromise(this.appExternalService.createApplication(KNOWLEDGE_BASE_FRAME_NAME), this.$q).then(
      frame => {
        this.$log.info('[' + MODULE_NAME + '] Page opened.');

        // this.externalAppService.insertApplication(KNOWLEDGE_BASE_FRAME_NAME, frame.ownerDocument.getElementById(KNOWLEDGE_BASE_FRAME_CONTAINER_NAME));
        // this.externalAppService.showApplication(KNOWLEDGE_BASE_FRAME_NAME);

        this.knowledgeBaseUrl = this.knowledgeBaseUrl + 'AKB/app.html?';

        this.knowledgeBaseFrame = this.replaceUrl(frame, this.knowledgeRequestId, this.knowledgeBaseUrl);
        return this.knowledgeBaseFrame;
      },
      () => {
        this.$log.error('[' + MODULE_NAME + '] Error while opening knowledge page.');
        // this.knowledgeBaseFrame = . forEach(knowledgeBaseFrame => knowledgeBaseFrame.reject());
      }
    );
  }

  private replaceUrl(frame: HTMLIFrameElement, knowledgeRequestId: string = '', url: string = ''): IPromise<HTMLIFrameElement> {
    let deferred = this.$q.defer<HTMLIFrameElement>();
    if (url === '') {
      url = this.knowledgeBaseUrl;
    }
    let myUrl = url;
    if (knowledgeRequestId !== '') {
      myUrl += '&id_ext=' + knowledgeRequestId;
    }
    let listener = () => {
      frame.removeEventListener('load', listener);
      if (document.getElementsByClassName('x-form-item-label-top').length > 0) {
        this.$log.error('[' + MODULE_NAME + '] Answers not found for this request id, fallback to generic url');
        frame.src = url;
      } else {
        this.$log.debug('[' + MODULE_NAME + '] call copyPasteToFrameFunction');
        // We found answers for this request, add javascript code for copy/paste functionality
        this.copyPasteToFrameFunction(knowledgeRequestId);
      }
      this.$log.debug('[' + MODULE_NAME + '] Frame loaded !');
      deferred.resolve(frame);
    };
    frame.addEventListener('load', listener);
    this.$log.info('[' + MODULE_NAME + '] Loading URL[' + myUrl + ']');
    frame.src = myUrl;
    return deferred.promise;
  }

  public getNbMDR(): IPromise<Number> {
    let deferred = this.$q.defer<Number>();
    let nb: Number = 0;
    if (!!this.knowledgeBaseFrame) {
      this.knowledgeBaseFrame.then(frame => {
        const container = frame.contentDocument;
        if (container) {
          let lblNb: Element = container.getElementsByClassName('x-panel-header-text')[0];
          if (!!lblNb) {
            let lbl = lblNb.textContent;
            if (!!lbl) {
              let from: number = lbl.indexOf(' ');
              if (from > 0) {
                nb = parseInt(lbl.substring(0, from), 10);
              }
            }
          }
        }
        deferred.resolve(nb);
      });
    } else {
      deferred.resolve(nb);
    }
    return deferred.promise;
  }

  /**
   * Get the text matching the button then paste it in the current eptica editor
   */
  private copyPasteToFrameFunction(knowledgeRequestId: string = '', nbCalls: number = 0): void {
    this.$log.debug(
      '[' + MODULE_NAME + '] copyPasteToFrameFunction in knowledgeRequestId[' + knowledgeRequestId + '] nbCalls[' + nbCalls + ']'
    );

    // is an eptica frame enabled ?
    let epticaFound: boolean = false;
    for (let e = 0; e < window.parent.frames.length; e++) {
      let myEpticaFrame = window.parent.frames[e];
      // get eptica frame
      if (!!myEpticaFrame.name && myEpticaFrame.name.substring(0, 14) === 'epticaRequest_') {
        epticaFound = true;
      }
    }

    // ugly fix for IE when eptica frame is not loaded fast enough
    if (
      knowledgeRequestId !== '' &&
      !epticaFound &&
      (navigator.userAgent.indexOf('MSIE') !== -1 || !!navigator.userAgent.match(/Trident.*rv\:11\./))
    ) {
      if (this.currentTimeout) {
        clearTimeout(this.currentTimeout);
        this.currentTimeout = null;
      }
      if (nbCalls < this.MAX_NB_CALLS) {
        this.currentTimeout = setTimeout(() => {
          this.copyPasteToFrameFunction(knowledgeRequestId, nbCalls++);
        }, this.TIME_BETWEEN_CALLS);
        this.$log.debug('[' + MODULE_NAME + '] copyPasteToFrameFunction out with setTimeout');
        return;
      }
    }

    this.$log.debug('[' + MODULE_NAME + '] copyPasteToFrameFunction epticaFound: ' + epticaFound);

    // inject callback into knowledgeBase frame
    for (let i = 0; epticaFound && i < window.parent.frames.length; i++) {
      let myFrame = window.parent.frames[i];
      this.$log.debug('[' + MODULE_NAME + '] myFrame.name: ' + myFrame.name);
      // get knowledgeBase frame
      if (myFrame.name === 'agentKnowledgeBase') {
        // this method is called each time the csr clicks on a green check
        let callbackFunction = contenu => {
          this.$log.debug('[' + MODULE_NAME + '] Receive sendClipboard event entering in callbackFunction');
          for (let e = 0; e < window.parent.frames.length; e++) {
            let myEpticaFrame = window.parent.frames[e];
            // get eptica frame
            if (myEpticaFrame.name.substring(0, 14) === 'epticaRequest_') {
              // get eptica editor
              let oEditor = (myEpticaFrame as any).getHtmlEditor(myEpticaFrame);
              if (oEditor == null) {
                this.$log.debug('oEditor was null');
                return;
              }

              let textToInsert: string = '';
              if (!!contenu) {
                if (!!contenu.data) {
                  if (typeof contenu.data === 'string' || contenu.data instanceof String) {
                    textToInsert = contenu.data as string;
                  } else {
                    this.$log.debug(
                      '[' + MODULE_NAME + '] Receive sendClipboard event but bad sub object attribute data type :' + typeof contenu.data
                    );
                  }
                } else if (typeof contenu === 'string' || contenu instanceof String) {
                  textToInsert = contenu as string;
                } else {
                  this.$log.debug(
                    '[' +
                      MODULE_NAME +
                      '] Receive sendClipboard event but bad object type :' +
                      typeof contenu +
                      ' value :' +
                      JSON.stringify(contenu)
                  );
                }
              } else {
                this.$log.debug('[' + MODULE_NAME + '] Receive sendClipboard event but object contenu was not defined');
              }

              if (textToInsert && textToInsert !== '') {
                this.$log.debug('[' + MODULE_NAME + '] Inserting text in Eptica editor :' + textToInsert);
                // insert the text to the eptica frame
                oEditor.insertText(contenu.data);
                oEditor.focus();
              } else {
                this.$log.debug('[' + MODULE_NAME + '] Not Inserting text in Eptica editor because text null or empty');
              }
            }
          }
        };
        this.$log.debug('[' + MODULE_NAME + "] Attach event listener 'sendClipboard' to frame");
        myFrame.document.addEventListener('sendClipboard', callbackFunction, false);
      }
    }

    this.$log.debug('[' + MODULE_NAME + '] copyPasteToFrameFunction out');
  }
}

export default MODULE_NAME;
